import React, { useEffect, memo } from 'react'
import { createRoot } from 'react-dom/client'

import Cross from './icons/cross.inline.svg'
import styles from './toast.module.scss'

interface ToastProps {
  message: string
  type?: 'success' | 'error' | 'info' | 'warning'
  duration?: number
  onClose?: () => void
}

const Toast = memo(({ message, type = 'info', duration = 3000, onClose }: ToastProps) => {
  useEffect(() => {
    const toastTimeout = setTimeout(() => {
      onClose?.()
    }, duration)

    return () => {
      clearTimeout(toastTimeout)
    }
  }, [duration, onClose])

  return (
    <div className={`${styles.toast} ${styles[type]}`}>
      <p>{message}</p>
      <Cross className={styles.iconContainer} onClick={onClose} />
    </div>
  )
})

Toast.displayName = 'Toast'

let toastContainer: HTMLElement | null = null
const getToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div')
    toastContainer.id = 'toast-container'
    Object.assign(toastContainer.style, {
      position: 'fixed',
      bottom: '1.5rem',
      zIndex: '9999',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '1rem',
    })
    document.body.appendChild(toastContainer)
  }
  return toastContainer
}

export const showToast = ({ message, type = 'info', duration = 3000 }: ToastProps) => {
  const container = getToastContainer()
  const toastElement = document.createElement('div')
  container.appendChild(toastElement)

  const root = createRoot(toastElement)

  const cleanup = () => {
    root.unmount()
    container.removeChild(toastElement)
    if (container.childElementCount === 0) {
      document.body.removeChild(container)
      toastContainer = null
    }
  }

  root.render(<Toast message={message} type={type} duration={duration} onClose={cleanup} />)
}

export default Toast
